import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  Interaction,
  SessionSource,
  DynamicResponseConfig,
} from '@terra/shared-contracts-interact';
import { terraProxyApiClient } from '../clients/testerClient';

export type InteractSession = {
  id: string;
  name: string;
  domain: string;
  expiresAt: string;
  createdAt: string;
  interactions: Interaction[];
  interactionCount: number;
  isActive: boolean;
  source: SessionSource;
  dynamicResponseConfig?: DynamicResponseConfig;
  dynamicUrl?: string;
};

type InteractStore = {
  sessions: InteractSession[];
  activeSessionId: string | null;
  selectedInteractionId: string | null;
  isGenerating: boolean;
  error: string | null;

  generateLink: () => Promise<string | null>;
  resumeSession: (sessionId: string) => Promise<boolean>;
  listSessions: () => Promise<void>;
  refreshInteractions: (sessionId: string) => Promise<void>;
  stopSession: (sessionId: string) => Promise<void>;
  deleteSession: (sessionId: string) => Promise<void>;
  setActiveSession: (sessionId: string | null) => void;
  setSelectedInteraction: (interactionId: string | null) => void;
  addInteraction: (sessionId: string, interaction: Interaction) => void;
  removeInteraction: (sessionId: string, interactionId: string) => void;
  clearInteractions: (sessionId: string) => void;
  markSessionStopped: (sessionId: string) => void;
  getActiveSession: () => InteractSession | undefined;
  getSelectedInteraction: () => Interaction | undefined;
  setDynamicResponse: (
    sessionId: string,
    config: DynamicResponseConfig,
  ) => Promise<string | null>;
  clearError: () => void;
};

export const useInteractStore = create<InteractStore>()(
  persist(
    (set, get) => ({
      sessions: [],
      activeSessionId: null,
      selectedInteractionId: null,
      isGenerating: false,
      error: null,

      generateLink: async () => {
        set({ isGenerating: true, error: null });

        try {
          const data = await terraProxyApiClient.generateInteractLink();

          if (!data) {
            throw new Error('Failed to generate link');
          }

          const newSession: InteractSession = {
            id: data.sessionId,
            name: `Session ${data.sessionId.slice(0, 8)}`,
            domain: data.domain,
            expiresAt: data.expiresAt,
            createdAt: new Date().toISOString(),
            interactions: [],
            interactionCount: 0,
            isActive: true,
            source: 'ui',
          };

          set((state) => ({
            sessions: [...state.sessions, newSession],
            activeSessionId: newSession.id,
            isGenerating: false,
          }));

          return newSession.id;
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          set({ error: errorMessage, isGenerating: false });
          return null;
        }
      },

      resumeSession: async (sessionId: string) => {
        set({ isGenerating: true, error: null });

        try {
          const data =
            await terraProxyApiClient.resumeInteractSession(sessionId);

          if (!data) {
            throw new Error('Failed to resume session');
          }

          set((state) => ({
            sessions: state.sessions.map((session) =>
              session.id === sessionId
                ? {
                    ...session,
                    domain: data.domain,
                    expiresAt: data.expiresAt,
                    isActive: true,
                  }
                : session,
            ),
            isGenerating: false,
          }));

          return true;
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          set({ error: errorMessage, isGenerating: false });
          return false;
        }
      },

      listSessions: async () => {
        try {
          const data = await terraProxyApiClient.listInteractSessions();
          if (!data) return;

          set((state) => {
            const remoteSessions = data.sessions.map((remoteSession) => {
              const localSession = state.sessions.find(
                (s) => s.id === remoteSession.sessionId,
              );
              return {
                id: remoteSession.sessionId,
                name:
                  localSession?.name ??
                  `Session ${remoteSession.sessionId.slice(0, 8)}`,
                domain: remoteSession.domain,
                expiresAt: remoteSession.expiresAt,
                createdAt: remoteSession.createdAt,
                interactions: localSession?.interactions ?? [],
                interactionCount: remoteSession.interactionCount,
                isActive: remoteSession.isActive,
                source: remoteSession.source,
                dynamicResponseConfig: localSession?.dynamicResponseConfig,
                dynamicUrl: localSession?.dynamicUrl,
              };
            });

            const remoteSessionIds = new Set(
              data.sessions.map((s) => s.sessionId),
            );

            const newActiveSessionId = state.activeSessionId
              ? remoteSessionIds.has(state.activeSessionId)
                ? state.activeSessionId
                : (remoteSessions[0]?.id ?? null)
              : null;

            const newSelectedInteractionId =
              newActiveSessionId === state.activeSessionId
                ? state.selectedInteractionId
                : null;

            return {
              sessions: remoteSessions,
              activeSessionId: newActiveSessionId,
              selectedInteractionId: newSelectedInteractionId,
            };
          });
        } catch {
          // noop
        }
      },

      refreshInteractions: async (sessionId: string) => {
        try {
          const data =
            await terraProxyApiClient.getInteractInteractions(sessionId);

          if (!data) {
            throw new Error('Failed to get interactions');
          }

          set((state) => ({
            sessions: state.sessions.map((session) =>
              session.id === sessionId
                ? {
                    ...session,
                    interactions: data.interactions,
                    interactionCount: data.interactions.length,
                  }
                : session,
            ),
          }));
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          set({ error: errorMessage });
        }
      },

      stopSession: async (sessionId: string) => {
        try {
          await terraProxyApiClient.stopInteractListener(sessionId);

          set((state) => ({
            sessions: state.sessions.map((session) =>
              session.id === sessionId
                ? { ...session, isActive: false }
                : session,
            ),
          }));
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          set({ error: errorMessage });
        }
      },

      deleteSession: async (sessionId: string) => {
        try {
          await terraProxyApiClient.deleteInteractSession(sessionId);

          set((currentState) => {
            const sessionToDelete = currentState.sessions.find(
              (s) => s.id === sessionId,
            );
            const remainingSessions = currentState.sessions.filter(
              (s) => s.id !== sessionId,
            );
            const wasActiveSession = currentState.activeSessionId === sessionId;

            const shouldClearSelection =
              currentState.selectedInteractionId &&
              sessionToDelete?.interactions.some(
                (i) => i.id === currentState.selectedInteractionId,
              );

            return {
              sessions: remainingSessions,
              activeSessionId: wasActiveSession
                ? (remainingSessions[0]?.id ?? null)
                : currentState.activeSessionId,
              selectedInteractionId: shouldClearSelection
                ? null
                : currentState.selectedInteractionId,
            };
          });
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          set({ error: errorMessage });
        }
      },

      setActiveSession: (sessionId: string | null) => {
        set({ activeSessionId: sessionId, selectedInteractionId: null });
      },

      setSelectedInteraction: (interactionId: string | null) => {
        set({ selectedInteractionId: interactionId });
      },

      addInteraction: (sessionId: string, interaction: Interaction) => {
        set((state) => ({
          sessions: state.sessions.map((session) => {
            if (session.id !== sessionId) return session;

            const exists = session.interactions.some(
              (i) => i.id === interaction.id,
            );
            if (exists) return session;

            return {
              ...session,
              interactions: [...session.interactions, interaction],
              interactionCount: session.interactionCount + 1,
            };
          }),
        }));
      },

      removeInteraction: (sessionId: string, interactionId: string) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? {
                  ...session,
                  interactions: session.interactions.filter(
                    (i) => i.id !== interactionId,
                  ),
                  interactionCount: Math.max(0, session.interactionCount - 1),
                }
              : session,
          ),
          selectedInteractionId:
            state.selectedInteractionId === interactionId
              ? null
              : state.selectedInteractionId,
        }));
      },

      clearInteractions: (sessionId: string) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? { ...session, interactions: [], interactionCount: 0 }
              : session,
          ),
          selectedInteractionId:
            state.activeSessionId === sessionId
              ? null
              : state.selectedInteractionId,
        }));
      },

      markSessionStopped: (sessionId: string) => {
        set((state) => ({
          sessions: state.sessions.map((session) =>
            session.id === sessionId
              ? { ...session, isActive: false }
              : session,
          ),
        }));
      },

      getActiveSession: () => {
        const state = get();
        return state.sessions.find((s) => s.id === state.activeSessionId);
      },

      getSelectedInteraction: () => {
        const state = get();
        const session = state.getActiveSession();
        return session?.interactions.find(
          (i) => i.id === state.selectedInteractionId,
        );
      },

      setDynamicResponse: async (
        sessionId: string,
        config: DynamicResponseConfig,
      ) => {
        try {
          const data = await terraProxyApiClient.setInteractDynamicResponse(
            sessionId,
            config,
          );

          if (!data) {
            throw new Error('Failed to set dynamic response');
          }

          set((state) => ({
            sessions: state.sessions.map((session) =>
              session.id === sessionId
                ? {
                    ...session,
                    dynamicResponseConfig: config,
                    dynamicUrl: data.dynamicUrl,
                  }
                : session,
            ),
          }));

          return data.dynamicUrl;
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          set({ error: errorMessage });
          return null;
        }
      },

      clearError: () => {
        set({ error: null });
      },
    }),
    {
      name: 'terra-proxy-interact-store',
      partialize: (state) => ({
        sessions: state.sessions.map((session) => ({
          ...session,
          interactions: session.interactions.slice(-100),
        })),
        activeSessionId: state.activeSessionId,
        selectedInteractionId: state.selectedInteractionId,
      }),
    },
  ),
);
